import { useState } from 'react';
import { Link } from '../../router/Link';
import { Button, Eyebrow, Thumbnail, cx } from '../../ui';
import { ThumbnailOverlay } from '../../listing/ThumbnailOverlay';
import { useDeArrowBranding } from '../../dearrow/useDeArrowBranding';
import { railPage, type ListingItem } from './model';
import styles from './recommendations.module.css';
import { useCopy } from '../../strings/useCopy';
import { SectionHead } from './SectionHead';

/**
 * §8's recommendations rail. Renders props: every href arrived built, every string arrived formatted,
 * and the one decision left here is whether a row has a badge to draw (R13).
 *
 * The rows are real anchors, so middle-click, ⌘-click and "copy link address" work on a recommendation
 * the way they do everywhere else. That costs a shape decision: the **whole row** is the video's link
 * while the channel name is a *different* one, and an anchor inside an anchor is invalid HTML
 * that browsers resolve by silently dropping one. So the title's anchor is stretched over the row by a
 * pseudo-element and the channel's is lifted back above it — one link per row for a keyboard and a
 * screen reader, the row as one target for a mouse, and both still real anchors.
 *
 * `className` exists because the Watch page places this in a grid *area* it owns. A module cannot
 * reach into another module's class, and that is the point — the layout stays with the layout.
 */

function RailRow({ item }: { item: ListingItem }) {
  const words = useCopy().text.watch.page;
  // DeArrow (2026-08-02) reaches the rail like any grid card — it is the *next* videos being
  // de-clickbaited; the page's own title above deliberately stays the record's.
  const dearrow = useDeArrowBranding(item.videoId);
  const title = dearrow.title ?? item.title;
  return (
    /* The grids' watched treatment, on the rail too: the row fades and the picture wears the
       mark. This list is where it earns the most — it is the one a reader clicks through. */
    <li className={cx(styles.row, item.watched && styles.watched)}>
      {/*
       * No longer a link of its own: the title's is stretched across the whole row, so the
       * picture is inside one target with everything else rather than being a second anchor to the
       * same place — one link per row for a keyboard and a screen reader, as before, and now the
       * mouse target is the row instead of two thirds of it.
       */}
      <span className={styles.thumb}>
        <Thumbnail
          src={dearrow.thumbnailUrl ?? item.thumbnailUrl}
          badge={item.badge}
          progress={item.progress}
          /* No `preview`: the rail runs no flipbook, so the corner note is only ever the mark. */
          overlay={<ThumbnailOverlay watched={item.watched} />}
        />
      </span>

      <div className={styles.text}>
        <Link className={styles.title} href={item.href} title={title}>
          {/* 4b's word, spoken rather than shown — the bar above is `aria-hidden`. */}
          {item.marker === null ? null : (
            <span className={styles.marker}>{item.marker.label}, </span>
          )}
          {title}
        </Link>
        <div className={styles.meta}>
          {/*
           * The author gets its own line so one long name cannot make a row three lines taller than
           * its neighbours — which a collaboration credit ("X and Dr. Y", `links/routes.ts`) does.
           */}
          <span className={styles.byline}>
            {item.authorHref === null ? (
              // No link, on purpose: a collaboration video has no single channel.
              <span className={styles.author} title={item.author}>
                {item.author}
              </span>
            ) : (
              <Link className={styles.author} href={item.authorHref} title={item.author}>
                {item.author}
              </Link>
            )}
            {item.authorVerified ? (
              <span className={styles.verified} title={words.verified}>
                ✓
              </span>
            ) : null}
          </span>
          {/* No view count, and that is measured rather than forgotten — see `toRailItems`. */}
          {item.publishedText === null ? null : (
            <span className={styles.published}>{item.publishedText}</span>
          )}
        </div>
      </div>
    </li>
  );
}

/** The pager's arrow — local, like every glyph only one component draws (`ui/icons.tsx`'s rule). */
function ChevronGlyph({ direction }: { readonly direction: 'back' | 'forward' }) {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" focusable="false">
      <path
        d={direction === 'back' ? 'M14.5 6 9 12l5.5 6' : 'M9.5 6 15 12l-5.5 6'}
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function Recommendations({
  items,
  onHide,
  className,
  stacked = false,
  shown = true,
}: {
  readonly items: readonly ListingItem[];
  /** The toggleable rail: the header carries the hide control; the way back is the caller's. */
  readonly onHide?: () => void;
  readonly className?: string;
  /**
   * Below the description rather than beside the video: the header wears the comments
   * section's shape — the heading with the toggle on the row's far end — and a hidden rail keeps
   * its header here, `shown` deciding only the body. Beside the video the caller hides the whole
   * panel instead, and the way back lives in the actions row.
   */
  readonly stacked?: boolean;
  readonly shown?: boolean;
}) {
  const rail = useCopy().text.watch.rail;
  // The window: at most four at once, walked with the pager under the list. Component state —
  // the window is not addressable — reset per video because the page is keyed by `videoId`.
  const [page, setPage] = useState(0);
  // An empty rail renders nothing at all. Every video sampled carried exactly twelve, so an empty one
  // means the response was thin — and a heading over a blank column reads as a broken page.
  if (items.length === 0) return null;

  const view = railPage(items, page);

  return (
    <aside className={cx(stacked ? styles.railStacked : styles.rail, className)} aria-label={rail.label}>
      {stacked ? (
        /*
         * The whole head presses — the heading is a button and the row's empty space forwards to
         * it. All of that is conditional on there being something to press: a caller with no `onHide`
         * has no toggle, and a heading dressed as a button that does nothing would be worse than a
         * heading. (In practice the stacked rail always has one; the prop is optional for the panel
         * beside the video, which hides the whole column instead.)
         */
        onHide === undefined ? (
          <div className={styles.headStacked}>
            <h2 className={styles.headingStacked}>{rail.heading}</h2>
          </div>
        ) : (
          <SectionHead
            className={styles.headStacked}
            headingClassName={styles.headingStacked}
            heading={rail.heading}
            shown={shown}
            onToggle={onHide}
          >
            <Button
              variant="subtle"
              className={styles.toggleStacked}
              label={shown ? rail.hideRecommendations : rail.showRecommendations}
              ariaExpanded={shown}
              onClick={onHide}
            >
              {shown ? rail.hide : rail.show}
            </Button>
          </SectionHead>
        )
      ) : (
        <div className={styles.head}>
          <Eyebrow>{rail.heading}</Eyebrow>
          {onHide === undefined ? null : (
            <Button variant="subtle" label={rail.hideRecommendations} onClick={onHide}>
              {rail.hide}
            </Button>
          )}
        </div>
      )}
      {!shown ? null : (
        <>
          <ul className={styles.list}>
            {view.items.map((item) => (
              <RailRow key={item.videoId} item={item} />
            ))}
          </ul>
          {view.pager === null ? null : (
            <div className={styles.pager}>
              {/* Round < > glyphs, centred under the window — the words live on as the
                  accessible names; a pager's direction is the one thing a glyph says faster. */}
              <Button
                variant="pill"
                size="sm"
                icon
                disabled={!view.pager.prevEnabled}
                label={rail.previousLabel}
                onClick={() => setPage(view.page - 1)}
              >
                <ChevronGlyph direction="back" />
              </Button>
              <span className={styles.pageText} aria-hidden="true">
                {view.pager.pageText}
              </span>
              <Button
                variant="pill"
                size="sm"
                icon
                disabled={!view.pager.nextEnabled}
                label={rail.nextLabel}
                onClick={() => setPage(view.page + 1)}
              >
                <ChevronGlyph direction="forward" />
              </Button>
            </div>
          )}
        </>
      )}
    </aside>
  );
}
